"use client"

import { useTranslations } from "next-intl"
import Image from "next/image"
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { createClient } from "@/utils/supabase/client"
import { useMobile } from "@/hooks/use-mobile"
import { ImageGalleryModal } from "@/components/chat/image-gallery-modal"
import { CancelPrivateListingDialog } from "@/components/chat/cancel-private-listing-dialog"
import { ReportDialog } from "@/components/ui/report-dialog"
import { DockSheet } from "@/components/ui/dock-sheet"
import { DialogCard } from "@/components/ui/dialog-card"
import { useAddToCart } from "@/hooks/use-add-to-cart"
import { useCartContext } from "@/components/cart/cart-provider"
import { editListingEvents } from "@/lib/edit-listing-events"
import { cn, formatPrice } from "@/lib/utils"
import { Ban, Flag, Loader2, Pencil, Truck } from "lucide-react"
import { Button } from "@/components/ui/button"
import { creationFlowPrimaryCtaClass } from "@/lib/creation-flow-form-styles"
import { SHEET_Z_NESTED } from "@/lib/sheet-styles"
import { DEFAULT_CURRENCY } from "@/lib/currencies"
import { SellerCheckoutNoteBanner } from "@/components/cart/seller-checkout-note-banner"
import { normalizeShippingConfig } from "@/lib/shipping-config"
import { parseListingImageUrls } from "@/lib/listings/parse-listing-images"
import { useListingSellerBanned } from "@/hooks/use-listing-seller-banned"
import { ListingViewTracker } from "@/components/analytics/listing-view-tracker"

type ListingDetailSheetProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  listing?: any
  listingId?: string
  messageId?: string
  roomName?: string
  currentUserId: string | null | undefined
  otherParticipantId?: string | null
  isOwnMessage?: boolean
  senderId?: string | null
  order?: any
  disabled?: boolean
  hideReport?: boolean
  embedded?: boolean
  onProceedToCheckout?: (opts: { listingId: string; addedInThisFlow: boolean }) => void
  onFooterChange?: (footer: ReactNode) => void
  onFixedSectionChange?: (section: ReactNode) => void
  counterpartySellerBanned?: boolean
}

const ListingDetailFooterContext = createContext<(node: React.ReactNode) => void>(() => {})
const ListingDetailFixedContext = createContext<(node: React.ReactNode) => void>(() => {})

const parseShippingConfig = (raw: any) => normalizeShippingConfig(raw)

function ListingPurchaseContent({
  listing,
  messageId,
  roomName,
  currentUserId,
  otherParticipantId,
  order,
  disabled,
  onClose,
  isMobile,
  onOpenReport,
  onProceedToCheckout,
  embedded = false,
  splitLayout = false,
  counterpartySellerBanned = false,
}: {
  listing: any
  messageId: string
  roomName: string
  currentUserId: string | null | undefined
  otherParticipantId?: string | null
  order?: any
  disabled?: boolean
  onClose: () => void
  isMobile?: boolean
  onOpenReport?: () => void
  onProceedToCheckout?: (opts: { listingId: string; addedInThisFlow: boolean }) => void
  embedded?: boolean
  splitLayout?: boolean
  counterpartySellerBanned?: boolean
}) {
  const userId = currentUserId ?? null
  const { cartItems, updateShippingMethod } = useCartContext()
  const { addToCart, adding, addError, clearAddError } = useAddToCart(userId)
  const listingId = String(listing?.id || "")
  const sellerBannedFromRpc = useListingSellerBanned(listingId || undefined, userId)
  const isInCart = Boolean(listingId && cartItems.some((i) => i.listingId === listingId))
  const tChat = useTranslations("chat")
  const title = String(listing?.title || "Listing")
  const description = String(listing?.description || "")
  const currencyCode = String((listing as any)?.currency || (listing as any)?.seller_currency || DEFAULT_CURRENCY).toUpperCase()
  const priceRaw = listing?.price
  const price = typeof priceRaw === "number" ? priceRaw : Number(priceRaw ?? 0)
  const quantityRaw = listing?.quantity
  const hasQuantity = !(quantityRaw === null || typeof quantityRaw === "undefined")
  const quantity = hasQuantity ? (typeof quantityRaw === "number" ? quantityRaw : Number(quantityRaw)) : Number.NaN
  const isQuantityKnown = hasQuantity && Number.isFinite(quantity)

  const isCancelled = Boolean(listing?.chat_private_cancelled_at)
  const isDeleted = Boolean(listing?.deleted_at)
  const hasOrder =
    Boolean(order) &&
    Boolean((order as any)?.orderId || (order as any)?.orderNumber || (order as any)?.sellerOrderId)
  const orderBuyerId = (order as any)?.userId ?? (order as any)?.user_id ?? null
  const isCurrentUserBuyer = Boolean(userId && orderBuyerId && userId === orderBuyerId)
  const isOtherParticipantBuyer = Boolean(otherParticipantId && orderBuyerId && otherParticipantId === orderBuyerId)
  const listingSellerId = String(listing?.user_id || "")
  const isSeller = Boolean(userId && listingSellerId && userId === listingSellerId)
  const sellerBanned = Boolean(sellerBannedFromRpc || (counterpartySellerBanned && !isSeller))
  const showPurchased = hasOrder && isCurrentUserBuyer
  const showSold = hasOrder && isSeller && isOtherParticipantBuyer
  const showGoneFromOrder = hasOrder && !showPurchased && !showSold
  const isOutOfStock = isQuantityKnown ? quantity <= 0 : false
  const isSold = hasOrder || (isOutOfStock && !isCancelled)
  const isGone = (isDeleted && !hasOrder) || showGoneFromOrder
  const isInactive = isSold || isCancelled || isGone
  const isUnavailable = !isQuantityKnown

  // Resolved at the parse step (not at the JSX): `images` fans out to `slides`,
  // `hero`, the gallery-modal payload and the checkout payload, and only some of
  // those pass through a thumb funnel. A bare storage path in `hero` crashes
  // next/image (`Failed to construct 'URL'`).
  const images = useMemo(() => parseListingImageUrls(listing?.image_urls), [listing])
  const slides = useMemo(() => (images.length > 0 ? images : ["/placeholder.svg"]), [images])
  const hero = slides[0] || "/placeholder.svg"

  const sellerStoreNotice = useMemo(() => {
    const prof = (listing as any)?.profiles
    const fromJoin = Array.isArray(prof) ? prof[0]?.checkout_note : prof?.checkout_note
    const raw =
      (listing as any)?.seller_checkout_note ??
      (typeof fromJoin === "string" ? fromJoin : null) ??
      (listing as any)?.checkout_note
    return typeof raw === "string" ? raw : ""
  }, [listing])

  const isLiveRate = (listing as any)?.shipping_mode === "live_rate"
  const shippingConfig = useMemo(() => parseShippingConfig(listing?.shipping_config), [listing])
  const derivedShippingOptions = useMemo(
    () =>
      shippingConfig
        .map((c) => ({
          id: String((c as any)?.shipping_method_id ?? (c as any)?.id ?? ""),
          label:
            typeof (c as any)?.label === "string"
              ? (c as any).label
              : typeof (c as any)?.name === "string"
                ? (c as any).name
                : "",
          price: Number((c as any)?.price ?? 0),
          allowCombined: Boolean((c as any)?.allow_combined),
          combinedFee: Number((c as any)?.combined_fee ?? 0),
        }))
        .filter((o) => Boolean(o.id) && o.id !== "undefined" && o.id !== "null"),
    [shippingConfig],
  )

  const [shippingOptions, setShippingOptions] = useState<Array<{ id: string; label: string; price: number; allowCombined: boolean; combinedFee: number }>>(
    derivedShippingOptions,
  )
  const [selectedShippingId, setSelectedShippingId] = useState<string | null>(null)
  const [galleryState, setGalleryState] = useState<{ images: { id: string; url: string; alt: string }[]; initialIndex: number } | null>(null)

  useEffect(() => {
    setSelectedShippingId(null)
  }, [listingId])

  useEffect(() => {
    setShippingOptions(derivedShippingOptions)
  }, [derivedShippingOptions])

  useEffect(() => {
    if (selectedShippingId || shippingOptions.length === 0) return
    const cartItem = cartItems.find((i) => i.listingId === listingId)
    const fromCart = cartItem?.selectedShippingMethod?.id
    if (fromCart && shippingOptions.some((o) => o.id === fromCart)) {
      setSelectedShippingId(fromCart)
    }
  }, [selectedShippingId, shippingOptions, cartItems, listingId])

  useEffect(() => {
    const ids = Array.from(new Set(shippingOptions.map((o) => o.id))).filter(Boolean)
    if (ids.length === 0) return
    let active = true
    ;(async () => {
      try {
        const res = await fetch("/api/shipping-methods/lookup", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids }),
        })
        if (!res.ok) return
        const json = (await res.json().catch(() => null)) as any
        const methods = Array.isArray(json?.methods) ? json.methods : []
        const map = new Map<string, { name: string; price: number }>()
        methods.forEach((m: any) => {
          const id = String(m?.id || "")
          if (!id) return
          map.set(id, { name: String(m?.name || ""), price: typeof m?.price === "number" ? m.price : Number(m?.price ?? 0) })
        })
        if (!active) return
        setShippingOptions((prev) =>
          prev.map((o) => {
            const m = map.get(o.id)
            if (!m) return o
            return { ...o, label: m.name || o.label, price: Number.isFinite(m.price) ? m.price : o.price }
          }),
        )
      } catch {}
    })()
    return () => {
      active = false
    }
  }, [shippingOptions])

  const selectedShipping = useMemo(() => shippingOptions.find((o) => o.id === selectedShippingId) ?? null, [selectedShippingId, shippingOptions])
  const ctaReady = Boolean(
    !disabled &&
      !isInactive &&
      !isSeller &&
      !isUnavailable &&
      !sellerBanned &&
      listingId &&
      (isInCart || (isQuantityKnown && quantity > 0)),
  )
  const canPurchase = Boolean(ctaReady && !adding)
  const soldLabel = showSold ? "SOLD" : showPurchased ? "PURCHASED" : isGone ? "GONE" : "SOLD"
  const purchasedChipLabel = isCancelled
    ? tChat("cancelled")
    : isGone
      ? tChat("gone")
      : showPurchased
        ? tChat("purchased")
        : showSold
          ? tChat("sold")
          : hasOrder
            ? tChat("gone")
            : tChat("sold")
  // Phase 4 (2026-06-29) of shippo-listing-discriminator: visibility enum replaces is_chat_private
  const isChatPrivate = (listing as any)?.visibility === "chat_private"
  const showSellerEdit = isSeller && !hasOrder && !isCancelled && !isGone
  const canCancel = Boolean(isSeller && isChatPrivate && !hasOrder && !isCancelled && !isDeleted && listingId)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)
  const supabase = useMemo(() => createClient(), [])
  const listingRef = useRef(listing)
  listingRef.current = listing

  const openEdit = useCallback(() => {
    if (!listingId) return
    editListingEvents.open({ listingId, listing: listingRef.current, messageId, roomName })
  }, [listingId, messageId, roomName])

  const cancelListing = useCallback(async () => {
    if (!canCancel || isCancelling) return
    setCancelError(null)
    setIsCancelling(true)
    try {
      const now = new Date().toISOString()
      const { error } = await supabase
        .from("listings")
        .update({ chat_private_cancelled_at: now })
        .eq("id", listingId)
        .select("id")
        .maybeSingle()
      if (error) {
        setCancelError(String(error.message || "Could not cancel this item."))
        return
      }
      setCancelDialogOpen(false)
      onClose()
    } finally {
      setIsCancelling(false)
    }
  }, [canCancel, isCancelling, listingId, onClose, supabase])

  const openHeroGallery = useCallback(() => {
    setGalleryState({
      images: slides.map((url, i) => ({ id: `${listingId}-img-${i}`, url, alt: `${title} ${i + 1}` })),
      initialIndex: 0,
    })
  }, [listingId, slides, title])

  const buyNow = useCallback(async () => {
    if (!listingId || isInactive || disabled || sellerBanned) return
    if (!isInCart && (!isQuantityKnown || quantity <= 0)) return

    /*
      Guard rail: never advance to the checkout step unless we can prove the
      item is on its way into the cart. Previously the flow always fired
      `onProceedToCheckout`, so any failure (no shipping method, RLS/502 on
      `addToCartAction`, `loadCart` race) silently produced a checkout screen
      with an empty cart — the "No items to checkout" UX the user reported.
    */
    const startedInCart = isInCart
    if (!startedInCart && !selectedShipping && !isLiveRate) {
      return
    }

    localStorage.setItem("kraahl:chat_buy_now_context", JSON.stringify({ roomName, messageId }))

    let addSucceeded = true
    if (!startedInCart) {
      const sellerId = String(listing?.user_id || "")
      const sellerName = String(listing?.seller_username || "Seller")
      const sellerAvatarUrl = listing?.seller_avatar_url ?? undefined
      const maxQuantity = Math.max(1, Number(listing?.quantity ?? 1))
      const shippingMethods = shippingConfig.map((c) => {
        const id = String((c as any)?.shipping_method_id ?? "")
        const option = shippingOptions.find((o) => o.id === id)
        const name = String((c as any)?.label || option?.label || id)
        return {
          id,
          name,
          description: "",
          price: Number((c as any)?.price ?? option?.price ?? 0),
          bannedCountries: [],
          allowCombinedShipping: Boolean((c as any)?.allow_combined),
          extraValue: Number((c as any)?.combined_fee ?? 0),
        }
      })
      try {
        await addToCart(
          {
            listingId,
            name: title,
            price: Number.isFinite(price) ? price : 0,
            currency: currencyCode,
            images,
            sellerId,
            sellerName,
            sellerAvatarUrl,
            condition: String(listing?.condition || ""),
            description,
            shippingMethods,
            maxQuantity,
          },
          { selectedShippingId: selectedShipping?.id ?? null, roomName, messageId },
        )
      } catch {
        addSucceeded = false
      }
    } else if (startedInCart && selectedShipping) {
      const cartItem = cartItems.find((i) => i.listingId === listingId)
      if (cartItem && cartItem.selectedShippingMethod?.id !== selectedShipping.id) {
        try {
          await updateShippingMethod(cartItem.id, selectedShipping.id)
        } catch {
          /* non-fatal: existing item stays with its current shipping */
        }
      }
    }

    if (!addSucceeded) {
      localStorage.removeItem("kraahl:chat_buy_now_context")
      return
    }

    if (onProceedToCheckout) {
      onProceedToCheckout({ listingId, addedInThisFlow: !startedInCart })
      return
    }
    window.dispatchEvent(
      new CustomEvent("open-chat-checkout", {
        detail: { listingId, messageId, roomName, addedInThisFlow: !startedInCart },
      }),
    )
    onClose()
  }, [addToCart, cartItems, currencyCode, description, disabled, images, isInCart, isInactive, isQuantityKnown, listing, listingId, messageId, onClose, onProceedToCheckout, price, quantity, roomName, selectedShipping, sellerBanned, shippingConfig, shippingOptions, title, updateShippingMethod])

  const buyNowRef = useRef(buyNow)
  buyNowRef.current = buyNow

  const heroPrice = useMemo(() => (Number.isFinite(price) ? formatPrice(price, currencyCode) : formatPrice(0, currencyCode)), [price, currencyCode])
  const totalPriceLabel = useMemo(() => {
    if (!selectedShipping) return heroPrice
    const total = (Number.isFinite(price) ? price : 0) + selectedShipping.price
    return formatPrice(total, currencyCode)
  }, [heroPrice, currencyCode, price, selectedShipping])

  const px = isMobile ? "" : "px-5"
  const dividerInset = isMobile ? "" : "mx-5"
  const contentWrapperCn = cn("w-full max-w-md mx-auto min-w-0", disabled && "opacity-70")
  const hasContentAfterReport = !isInactive && !showSellerEdit && !isUnavailable && !(sellerBanned && !isSeller) && !isLiveRate

  const footerNode = useMemo(() => {
    if (sellerBanned && !isSeller && !isInactive) {
      return (
        <div className="w-full">
          <div className="w-full rounded-full h-12 text-[15px] font-medium bg-muted text-muted-foreground/70 flex items-center justify-center">
            {tChat("userBanned")}
          </div>
        </div>
      )
    }
    if (!isInactive && !showSellerEdit && !isUnavailable) {
      return (
        <div className="w-full flex flex-col gap-2">
          {addError ? (
            <p className="text-sm text-red-400 text-center" role="alert">
              {addError}
            </p>
          ) : null}
          <Button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              clearAddError()
              void buyNowRef.current()
            }}
            disabled={adding || (!isLiveRate && !selectedShippingId)}
            className={creationFlowPrimaryCtaClass}
            size="lg"
          >
            {!isLiveRate && !selectedShippingId ? (
              tChat("selectShipping")
            ) : adding ? (
              <span className="inline-flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                {tChat("payNow")} - {totalPriceLabel}
              </span>
            ) : (
              `${tChat("payNow")} - ${totalPriceLabel}`
            )}
          </Button>
        </div>
      )
    }
    if (showSellerEdit || canCancel) {
      return (
        <div className="w-full flex flex-col gap-2">
          {showSellerEdit ? (
            <Button
              type="button"
              onClick={openEdit}
              className={creationFlowPrimaryCtaClass}
              size="lg"
            >
              <Pencil className="w-4 h-4" />
              {tChat("edit")}
            </Button>
          ) : null}
          {canCancel ? (
            <Button
              type="button"
              onClick={() => setCancelDialogOpen(true)}
              disabled={isCancelling}
              variant="outline"
              className={cn(
                "w-full rounded-full h-12 border-foreground/20 bg-muted/80 text-foreground hover:bg-sheet-handle hover:border-foreground/30",
                isCancelling && "opacity-60"
              )}
            >
              <Ban className="w-4 h-4" />
              {tChat("cancelItem")}
            </Button>
          ) : null}
        </div>
      )
    }
    if (isUnavailable && !isSeller) {
      return (
        <div className="w-full">
          <div className="w-full rounded-full h-12 text-[15px] font-medium bg-muted text-muted-foreground/70 flex items-center justify-center">
            {tChat("unavailable")}
          </div>
        </div>
      )
    }
    return (
      <div className="w-full">
        <div className="w-full rounded-full h-12 text-[15px] font-medium bg-muted text-muted-foreground/70 flex items-center justify-center">
          {isCancelled ? tChat("cancelled") : isGone ? tChat("gone") : purchasedChipLabel}
        </div>
      </div>
    )
  }, [
    addError,
    adding,
    canCancel,
    clearAddError,
    isCancelling,
    isCancelled,
    isGone,
    isMobile,
    isSeller,
    isUnavailable,
    sellerBanned,
    purchasedChipLabel,
    selectedShippingId,
    isInCart,
    showSellerEdit,
    totalPriceLabel,
    isInactive,
    isLiveRate,
  ])

  const setFooter = useContext(ListingDetailFooterContext)
  const setFixedSection = useContext(ListingDetailFixedContext)

  const fixedBlockNode = useMemo(
    () => (
      <div className={contentWrapperCn}>
        <div className={cn("pt-2 pb-4", px)}>
          <div className="flex gap-3.5 min-w-0">
            <button
              type="button"
              onClick={openHeroGallery}
              className={cn(
                "relative rounded-2xl overflow-hidden shrink-0 bg-muted active:scale-[0.98] transition-transform",
                isMobile ? "size-24" : "size-32"
              )}
              disabled={slides.length === 0}
            >
              <Image
                src={hero}
                alt={title}
                fill
                className="object-cover"
                sizes={isMobile ? "96px" : "128px"}
              />
            </button>
            <div className="flex-1 flex flex-col justify-center gap-1 min-w-0 overflow-hidden">
              {(isMobile || embedded) && <h3 className="text-[17px] font-semibold text-foreground truncate">{title}</h3>}
              {description ? (
                <p className="text-[13px] text-muted-foreground leading-snug line-clamp-2">{description}</p>
              ) : null}
              <p className={cn("font-bold text-foreground", isMobile ? "text-[22px]" : "text-[28px]")}>{heroPrice}</p>
            </div>
          </div>
          <SellerCheckoutNoteBanner note={sellerStoreNotice} className="mt-3" />
        </div>
        {(onOpenReport || hasContentAfterReport) ? (
          <div className={cn("h-px bg-muted", dividerInset)} />
        ) : null}
        {onOpenReport ? (
          <div className={cn("py-3", px)}>
            <button
              type="button"
              onClick={onOpenReport}
              className="flex items-center gap-2 text-[13px] text-red-400 active:text-red-300 hover:text-red-300 transition-colors"
            >
              <Flag className="w-3.5 h-3.5" />
              {tChat("reportListing")}
            </button>
          </div>
        ) : null}
        {onOpenReport && hasContentAfterReport ? (
          <div className={cn("h-px bg-muted", dividerInset)} />
        ) : null}
      </div>
    ),
    [
      contentWrapperCn,
      description,
      embedded,
      hasContentAfterReport,
      sellerStoreNotice,
      hero,
      heroPrice,
      isMobile,
      onOpenReport,
      openHeroGallery,
      dividerInset,
      px,
      title,
    ]
  )

  useEffect(() => {
    if (!splitLayout) {
      setFixedSection(null)
      return
    }
    setFixedSection(fixedBlockNode)
  }, [splitLayout, setFixedSection, fixedBlockNode])

  useEffect(() => {
    setFooter(footerNode)
  }, [
    setFooter,
    addError,
    adding,
    canCancel,
    clearAddError,
    isCancelling,
    isCancelled,
    isGone,
    isInactive,
    isMobile,
    isSeller,
    isUnavailable,
    sellerBanned,
    purchasedChipLabel,
    selectedShippingId,
    isInCart,
    showSellerEdit,
    totalPriceLabel,
    isLiveRate,
    footerNode,
  ])

  const scrollPart = (
    <>
      {!isInactive && !showSellerEdit && !isUnavailable && !(sellerBanned && !isSeller) && !isLiveRate ? (
          <div className={cn("py-4", px)}>
            <h4 className="text-[13px] font-semibold text-muted-foreground/70 uppercase tracking-wider mb-2.5">{tChat("shipping")}</h4>
            <div className="space-y-2">
              {shippingOptions.map((option) => {
                const isActive = option.id === selectedShippingId
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => {
                      clearAddError()
                      setSelectedShippingId(option.id)
                    }}
                    className={cn(
                      "w-full flex items-center justify-between px-4 py-3 rounded-2xl transition-all",
                      isActive ? "bg-primary text-primary-foreground" : "bg-muted text-foreground hover:bg-sheet-handle active:bg-sheet-handle"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <Truck className="w-4.5 h-4.5" />
                      <div className="text-left">
                        <p className="text-[14px] font-medium">{option.label || tChat("shipping")}</p>
                      </div>
                    </div>
                    <p className="text-[14px] font-bold">{formatPrice(option.price, currencyCode)}</p>
                  </button>
                )
              })}
            </div>
          </div>
        ) : null}
    </>
  )

  if (splitLayout) {
    return (
      <>
        <CancelPrivateListingDialog
          open={cancelDialogOpen}
          onOpenChange={(open) => {
            setCancelDialogOpen(open)
            if (!open) setCancelError(null)
          }}
          isProcessing={isCancelling}
          errorMessage={cancelError}
          onConfirm={cancelListing}
        />
        <div className={contentWrapperCn}>
          {scrollPart}
        </div>
        <ImageGalleryModal
          images={galleryState?.images ?? []}
          isOpen={!!galleryState}
          onCloseAction={() => setGalleryState(null)}
          initialIndex={galleryState?.initialIndex ?? 0}
        />
      </>
    )
  }

  return (
    <>
      <CancelPrivateListingDialog
        open={cancelDialogOpen}
        onOpenChange={(open) => {
          setCancelDialogOpen(open)
          if (!open) setCancelError(null)
        }}
        isProcessing={isCancelling}
        errorMessage={cancelError}
        onConfirm={cancelListing}
      />
      <div className={contentWrapperCn}>
        <div className={cn("pt-2 pb-4", px)}>
          <div className="flex gap-3.5 min-w-0">
            <button
              type="button"
              onClick={openHeroGallery}
              className={cn(
                "relative rounded-2xl overflow-hidden shrink-0 bg-muted active:scale-[0.98] transition-transform",
                isMobile ? "size-24" : "size-32"
              )}
              disabled={slides.length === 0}
            >
              <Image
                src={hero}
                alt={title}
                fill
                className="object-cover"
                sizes={isMobile ? "96px" : "128px"}
              />
            </button>
            <div className="flex-1 flex flex-col justify-center gap-1 min-w-0 overflow-hidden">
              {(isMobile || embedded) && <h3 className="text-[17px] font-semibold text-foreground truncate">{title}</h3>}
              {description ? (
                <p className="text-[13px] text-muted-foreground leading-snug line-clamp-2">{description}</p>
              ) : null}
              <p className={cn("font-bold text-foreground", isMobile ? "text-[22px]" : "text-[28px]")}>{heroPrice}</p>
            </div>
          </div>

          <SellerCheckoutNoteBanner note={sellerStoreNotice} className="mt-3" />
        </div>

        {(onOpenReport || hasContentAfterReport) ? (
          <div className={cn("h-px bg-muted", dividerInset)} />
        ) : null}

        {onOpenReport ? (
          <div className={cn("py-3", px)}>
            <button
              type="button"
              onClick={onOpenReport}
              className="flex items-center gap-2 text-[13px] text-red-400 active:text-red-300 hover:text-red-300 transition-colors"
            >
              <Flag className="w-3.5 h-3.5" />
              {tChat("reportListing")}
            </button>
          </div>
        ) : null}

        {onOpenReport && hasContentAfterReport ? (
          <div className={cn("h-px bg-muted", dividerInset)} />
        ) : null}

        {!isInactive && !showSellerEdit && !isUnavailable && !(sellerBanned && !isSeller) && !isLiveRate ? (
          <div className={cn("py-4", px)}>
            <h4 className="text-[13px] font-semibold text-muted-foreground/70 uppercase tracking-wider mb-2.5">{tChat("shipping")}</h4>
            <div className="space-y-2">
              {shippingOptions.map((option) => {
                const isActive = option.id === selectedShippingId
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => {
                      clearAddError()
                      setSelectedShippingId(option.id)
                    }}
                    className={cn(
                      "w-full flex items-center justify-between px-4 py-3 rounded-2xl transition-all",
                      isActive ? "bg-primary text-primary-foreground" : "bg-muted text-foreground hover:bg-sheet-handle active:bg-sheet-handle"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <Truck className="w-4.5 h-4.5" />
                      <div className="text-left">
                        <p className="text-[14px] font-medium">{option.label || tChat("shipping")}</p>
                      </div>
                    </div>
                    <p className="text-[14px] font-bold">{formatPrice(option.price, currencyCode)}</p>
                  </button>
                )
              })}
            </div>
          </div>
        ) : null}
      </div>

      <ImageGalleryModal
        images={galleryState?.images ?? []}
        isOpen={!!galleryState}
        onCloseAction={() => setGalleryState(null)}
        initialIndex={galleryState?.initialIndex ?? 0}
      />
    </>
  )
}

function fetchListingById(listingId: string): Promise<any> {
  const supabase = createClient()
  return Promise.resolve(
    supabase
      .from("listings")
    .select(
      "id, user_id, title, description, price, currency, quantity, image_urls, condition, grade, shipping_config, shipping_mode, chat_private_cancelled_at, deleted_at, visibility, is_unlisted, profiles:user_id(username, avatar_url, currency, checkout_note)"
    )
    .eq("id", listingId)
    .single()
    .then(
      ({ data, error }) => {
        if (error || !data) return null
        const profile = Array.isArray((data as any).profiles) ? (data as any).profiles[0] : (data as any).profiles
        return {
          ...data,
          seller_username: profile?.username ?? null,
          seller_avatar_url: profile?.avatar_url ?? null,
          seller_currency: profile?.currency ?? null,
          seller_checkout_note: typeof profile?.checkout_note === "string" ? profile.checkout_note : null,
        }
      },
      () => null
    )
  )
}

export function ChatListingDetailSheet({
  isOpen,
  onOpenChange,
  listing: listingProp,
  listingId: listingIdProp,
  messageId = "",
  roomName = "",
  currentUserId,
  otherParticipantId,
  isOwnMessage,
  senderId,
  order,
  disabled,
  hideReport = false,
  embedded = false,
  onProceedToCheckout,
  onFooterChange,
  onFixedSectionChange,
  counterpartySellerBanned = false,
}: ListingDetailSheetProps) {
  const isMobile = useMobile()
  const [reportOpen, setReportOpen] = useState(false)
  const [fetchedListing, setFetchedListing] = useState<any | null>(null)
  const [loading, setLoading] = useState(false)
  const handleClose = useCallback(() => onOpenChange(false), [onOpenChange])

  const listingIdForFetch = String((listingProp as any)?.id || listingIdProp || "")
  const shouldFetchListing = Boolean((isOpen || embedded) && listingIdForFetch)

  useEffect(() => {
    if (!shouldFetchListing) {
      if (!embedded) setFetchedListing(null)
      return
    }
    setLoading(true)
    let active = true
    fetchListingById(listingIdForFetch).then((data) => {
      if (!active) return
      setFetchedListing(data)
      setLoading(false)
    })
    return () => {
      active = false
    }
  }, [shouldFetchListing, listingIdForFetch, isOpen, embedded, messageId])

  useEffect(() => {
    if (!isOpen && !embedded) setFetchedListing(null)
  }, [isOpen, embedded])

  const listing = useMemo(() => {
    if (!listingProp && fetchedListing) return fetchedListing
    if (listingProp && fetchedListing) return { ...listingProp, ...fetchedListing }
    return listingProp ?? fetchedListing ?? null
  }, [listingProp, fetchedListing])
  const listingId = String(listing?.id || listingIdProp || "")

  const [listingFooter, setListingFooter] = useState<ReactNode>(null)
  const [listingFixedSection, setListingFixedSection] = useState<ReactNode>(null)
  useEffect(() => {
    setListingFooter(null)
    setListingFixedSection(null)
  }, [listingId])

  useEffect(() => {
    if (!onFooterChange) return
    onFooterChange(listingFooter)
    return () => onFooterChange(null)
  }, [onFooterChange, listingFooter])

  useEffect(() => {
    if (!onFixedSectionChange) return
    onFixedSectionChange(listingFixedSection)
    return () => onFixedSectionChange(null)
  }, [onFixedSectionChange, listingFixedSection])

  const listingSellerId = listing?.user_id ?? null
  const fromFeed = Boolean(listingIdProp && !listingProp)
  const effectiveHideReport = hideReport || fromFeed
  const canReportListing = !effectiveHideReport && Boolean(listingId) && Boolean(currentUserId) && listingSellerId !== currentUserId
  const handleOpenReport = useCallback(() => setReportOpen(true), [])

  const inDarkSheetBody = isMobile

  const showLoader = Boolean(!listingProp && listingIdForFetch && (loading || !listing))
  // Demand-funnel B1: a chat-sheet open showing a listing counts as a view
  // (surface='chat_sheet'). Mounted INSIDE listingScrollContent so it only
  // exists while the listing is actually displayed: both render sites
  // unmount this content while closed (chat-message.tsx non-embedded path
  // via `if (!isOpen) return null` below; GlobalQuickCheckout.tsx embedded
  // path renders the whole sheet only when `sheetOpen && step === "detail"`),
  // and the loader state renders nothing. Re-open = remount = a new view
  // (by design — no write-time dedup).
  const listingScrollContent =
    listing && !showLoader ? (
      <ListingDetailFooterContext.Provider value={setListingFooter}>
        <ListingDetailFixedContext.Provider value={setListingFixedSection}>
          {listingId ? <ListingViewTracker listingId={listingId} surface="chat_sheet" /> : null}
          <div className="min-w-0">
          <ListingPurchaseContent
            listing={listing}
            messageId={messageId}
            roomName={roomName}
            currentUserId={currentUserId}
            otherParticipantId={otherParticipantId}
            order={order}
            disabled={disabled}
            onClose={handleClose}
            isMobile={isMobile}
            onOpenReport={canReportListing ? handleOpenReport : undefined}
            onProceedToCheckout={onProceedToCheckout}
            embedded={embedded}
            splitLayout={Boolean(isMobile)}
            counterpartySellerBanned={counterpartySellerBanned}
          />
        </div>
        </ListingDetailFixedContext.Provider>
      </ListingDetailFooterContext.Provider>
    ) : null
  const bodyContentMobile = showLoader ? (
    <div className="flex flex-col items-center justify-center min-h-[280px] px-6">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  ) : listing ? (
    <div data-report-dialog>{listingScrollContent}</div>
  ) : null
  // Desktop is now the same shape as mobile: plain scroll content, with the
  // scroll reserve + gradient + floating CTA supplied by the primitive's
  // `footer` slot. `data-report-dialog` sits on the PANEL (see the desktop
  // branch), so this wrapper carries layout only.
  const bodyContentDesktop = showLoader ? (
    <div className="flex flex-col items-center justify-center min-h-[280px] px-6">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  ) : listing ? (
    <div className="min-w-0">{listingScrollContent}</div>
  ) : null

  if (embedded) {
    const embeddedContent = showLoader ? (
      <div className="flex flex-col items-center justify-center min-h-[280px] px-6">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    ) : listing ? (
      listingScrollContent
    ) : null

    if (onFooterChange) {
      return (
        <div data-report-dialog className="min-w-0">
          {embeddedContent}
        </div>
      )
    }

    return (
      <div data-report-dialog className="flex min-h-0 flex-1 flex-col">
        {showLoader ? (
          <div className="flex flex-col items-center justify-center min-h-[280px] px-6">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : listing ? (
          <div className="relative flex min-h-0 flex-1 flex-col min-w-0">
            <div
              className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden min-w-0 scrollbar-none [&::-webkit-scrollbar]:hidden"
              style={{ paddingBottom: "calc(6rem + env(safe-area-inset-bottom))" }}
            >
              {listingScrollContent}
            </div>
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-20 bg-linear-to-t from-sheet to-transparent"
              aria-hidden
            />
            <div
              className={cn(
                "absolute bottom-0 left-0 right-0 z-20 flex flex-col pt-4 text-foreground",
                inDarkSheetBody ? "px-0" : "px-4",
              )}
              style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {listingFooter}
            </div>
          </div>
        ) : null}
      </div>
    )
  }

  if (!isOpen) return null

  if (isMobile) {
    return (
      <>
        <DockSheet
          open={isOpen}
          onOpenChange={onOpenChange}
          title={listing?.title ?? "Listing"}
          zIndex={SHEET_Z_NESTED}
          belowTitle={listing && !showLoader ? listingFixedSection : undefined}
          footer={listing && !showLoader ? listingFooter : undefined}
        >
          {bodyContentMobile}
        </DockSheet>

        {listing && canReportListing && (
          <ReportDialog
            open={reportOpen}
            onOpenChangeAction={setReportOpen}
            itemType="listing"
            itemId={listingId}
            itemTitle={listing?.title ? `Listing: ${listing.title}` : undefined}
            currentUserId={currentUserId ?? undefined}
            reportedUserId={listingSellerId ?? undefined}
            reportedUsername={(listing as any)?.seller_username ?? (listing as any)?.profiles?.username ?? undefined}
            reportedAvatarUrl={(listing as any)?.seller_avatar_url ?? (listing as any)?.profiles?.avatar_url ?? null}
          />
        )}
      </>
    )
  }

  return (
    <>
      <DialogCard
        open={isOpen}
        onOpenChange={onOpenChange}
        title={listing?.title ?? "Listing"}
        panelClassName="max-w-md max-h-[85vh] shadow-2xl"
        contentClassName="px-0 pt-0 min-w-0"
        footer={listing && !showLoader ? listingFooter : undefined}
        /* On the PANEL, not on a body wrapper: the chat shell keeps itself
           open by testing `closest("[data-report-dialog]")` on the
           outside-click target, and a click on this card's CHROME row has to
           be covered by that test too. */
        panelDataAttributes={{ "data-report-dialog": "" }}
      >
        {bodyContentDesktop}
      </DialogCard>

      {listing && canReportListing && (
        <ReportDialog
          open={reportOpen}
          onOpenChangeAction={setReportOpen}
          itemType="listing"
          itemId={listingId}
          itemTitle={listing?.title ? `Listing: ${listing.title}` : undefined}
          currentUserId={currentUserId ?? undefined}
          reportedUserId={listingSellerId ?? undefined}
          reportedUsername={(listing as any)?.seller_username ?? (listing as any)?.profiles?.username ?? undefined}
          reportedAvatarUrl={(listing as any)?.seller_avatar_url ?? (listing as any)?.profiles?.avatar_url ?? null}
        />
      )}
    </>
  )
}
